import { type DefaultContext, mutationPromise } from '@egw/clients'
import { createMutationHook, createQueryHook } from '../../../hooks'
import {
  type EnrollmentVerifyPhoneData,
  type EnrollmentVerifyPhoneGetInput,
  type EnrollmentVerifyPhoneLinkSubmitInput,
  type EnrollmentVerifyPhoneLinkSubmitOutput,
  type EnrollmentVerifyPhoneOtpSubmitInput,
  type EnrollmentVerifyPhoneOtpSubmitOutput,
} from '../../../models/enrollment/generic/verify-phone'

/**
 * Get Enrollment  VerifyPhone data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentVerifyPhoneData>
 * @example const response = await getEnrollmentVerifyPhoneData()
 */
export const getEnrollmentVerifyPhoneData = async (
  enrollmentVerifyPhoneGetInput?: EnrollmentVerifyPhoneGetInput,
  context?: DefaultContext,
): Promise<EnrollmentVerifyPhoneData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentVerifyPhoneData',
      query: `mutation getEnrollmentVerifyPhoneData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
              phoneNumber
              progress
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentVerifyPhoneGetInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentVerifyPhoneData }
  return response.submitEnrollment
}

export const useGetEnrollmentVerifyPhoneData = createQueryHook<
  EnrollmentVerifyPhoneGetInput,
  EnrollmentVerifyPhoneData
>(getEnrollmentVerifyPhoneData)

/**
 * Submit Enrollment  VerifyPhone data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentVerifyPhoneSubmitOutput>
 * @example const response = await submitEnrollmentVerifyPhoneData();
 */
export const submitEnrollmentVerifyPhoneLinkData = async (
  enrollmentVerifyPhoneSubmitInput: EnrollmentVerifyPhoneLinkSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentVerifyPhoneLinkSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentVerifyPhoneData',
      query: `mutation submitEnrollmentVerifyPhoneData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentVerifyPhoneSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentVerifyPhoneLinkSubmitOutput }
  return response.submitEnrollment
}

/**
 * Submit Enrollment  VerifyPhone data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentVerifyPhoneSubmitOutput>
 * @example const response = await submitEnrollmentVerifyPhoneData();
 */
export const submitEnrollmentVerifyPhoneOtpData = async (
  enrollmentVerifyPhoneSubmitInput: EnrollmentVerifyPhoneOtpSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentVerifyPhoneOtpSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentVerifyPhoneData',
      query: `mutation submitEnrollmentVerifyPhoneData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentVerifyPhoneSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentVerifyPhoneOtpSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentVerifyPhoneLinkData = createMutationHook<
  EnrollmentVerifyPhoneLinkSubmitInput,
  EnrollmentVerifyPhoneLinkSubmitOutput
>(submitEnrollmentVerifyPhoneLinkData)

export const useSubmitEnrollmentVerifyPhoneOTPData = createMutationHook<
  EnrollmentVerifyPhoneOtpSubmitInput,
  EnrollmentVerifyPhoneOtpSubmitOutput
>(submitEnrollmentVerifyPhoneOtpData)
